import { t } from "@lingui/macro";
import { round, startCase } from "lodash";

import { WeatherIcon } from "@whoosh/design-system/src/components/icons_v2/weather";
import { WeatherStatus } from "@whoosh/design-system/src/components/icons_v2/weather/components/types";
import {
  HStack,
  Center,
  Text,
} from "@whoosh/design-system/src/components/primitive";

export type WeatherConditionType = {
  temperature: number;
  windSpeedInMph: number;
  measurementSystem: "IMPERIAL" | "METRIC";
  weatherCategory: WeatherStatus;
};

export const getWeatherCategoryLabel = (weatherCategory: string) => {
  switch (weatherCategory) {
    case "ASH":
      return t({ message: "Ash", context: "Weather condition" });
    case "CLEAR":
      return t({ message: "Clear", context: "Weather condition" });
    case "CLOUDS":
      return t({ message: "Clouds", context: "Weather condition" });
    case "DRIZZLE":
      return t({ message: "Drizzle", context: "Weather condition" });
    case "DUST":
      return t({ message: "Dust", context: "Weather condition" });
    case "FOG":
      return t({ message: "Fog", context: "Weather condition" });
    case "HAZE":
      return t({ message: "Haze", context: "Weather condition" });
    case "MIST":
      return t({ message: "Mist", context: "Weather condition" });
    case "RAIN":
      return t({ message: "Rain", context: "Weather condition" });
    case "SAND":
      return t({ message: "Sand", context: "Weather condition" });
    case "SMOKE":
      return t({ message: "Smoke", context: "Weather condition" });
    case "SNOW":
      return t({ message: "Snow", context: "Weather condition" });
    case "SQUALL":
      return t({ message: "Squall", context: "Weather condition" });
    case "THUNDERSTORM":
      return t({ message: "Thunderstorm", context: "Weather condition" });
    case "TORNADO":
      return t({ message: "Tornado", context: "Weather condition" });
    default:
      return startCase(weatherCategory.toLowerCase());
  }
};

type WeatherConditionProps = {
  weatherCondition?: WeatherConditionType | null;
};

export const WeatherCondition = ({
  weatherCondition,
}: WeatherConditionProps) => {
  const tempUnit = weatherCondition?.measurementSystem === "METRIC" ? "C" : "F";
  const windUnit =
    weatherCondition?.measurementSystem === "METRIC" ? "m/s" : "mph";
  const weatherString = weatherCondition
    ? `${getWeatherCategoryLabel(weatherCondition.weatherCategory)}, ${round(
        weatherCondition.temperature
      )}° ${tempUnit}, ${t`Wind`}: ${round(
        weatherCondition.windSpeedInMph
      )} ${windUnit}`
    : undefined;

  return weatherCondition ? (
    <HStack gap={2} alignItems="center" testID="weather-condition">
      <Center bg="surface-primary" width={16} height={16}>
        <WeatherIcon
          iconProps={{ color: "icon-primary" }}
          weatherCategory={weatherCondition.weatherCategory}
        />
      </Center>
      <Text variant="mainSmallSemiBold">{weatherString}</Text>
    </HStack>
  ) : null;
};
